import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { searchProducts } from "@/lib/api";

/**
 * Right-column carousel widget on Dealer Home — replaces the old static
 * "Performance Essentials" card. Loads products where Airtable's
 * `Featured` checkbox is on (via `/postcue/products/search?featured_only=true`)
 * and renders a single-product card at a time with prev/next arrows and dot
 * indicators, matching the mindfulminerals.com sidebar.
 */
export default function FeaturedSidebar({ dealerSlug }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const res = await searchProducts("", 10, { featured_only: true });
        if (!cancel) setItems(res?.products || []);
      } catch {
        if (!cancel) setItems([]);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, []);

  if (loading || items.length === 0) return null;

  const safeIdx = ((idx % items.length) + items.length) % items.length;
  const current = items[safeIdx];
  const productHref = dealerSlug
    ? `/d/${dealerSlug}/p/${current.slug}`
    : `/p/${current.slug}`;
  const shopHref = dealerSlug ? `/d/${dealerSlug}?view=shop` : "/d/orevital?view=shop";

  return (
    <div className="space-y-3" data-testid="featured-sidebar-wrap">
      {/* Light violet brand strip — exact same template as the green
          "Mindful Minerals | OreVital" strip on the left column:
          one row, big bold left + small caption right. */}
      {/* Light violet brand strip — exact same template as the green strip
          on the left column. */}
      <div className="rounded-t-2xl bg-violet-200/70 px-5 py-3 flex items-center">
        <div className="font-semibold text-violet-900 tracking-tight whitespace-nowrap">
          Exclusive Wellness &amp; Peptide Innovations
        </div>
      </div>

      {/* Dark violet hero — matched in height to the dark hero on the left
          column. Heading scaled down so the narrow 320px sidebar doesn't
          balloon vertical height when text wraps. */}
      <div className="rounded-2xl bg-gradient-to-br from-violet-700 to-violet-900 text-white px-5 py-7 min-h-[180px] sm:min-h-[200px] flex flex-col justify-center">
        <h2 className="text-xl font-semibold tracking-tight leading-tight">
          Meet the <span className="text-amber-300">Family</span> of Performance Essentials
        </h2>
        <p className="mt-2 text-xs text-white/85 leading-relaxed">
          Devices and transdermal skin care powered by{" "}
          <span className="text-amber-300 font-semibold">MgCl<sub>2</sub></span>{" "}
          and peptides
        </p>
      </div>

      {/* Featured product carousel */}
      <div
        className="rounded-2xl overflow-hidden border border-zinc-200 bg-white"
        data-testid="featured-sidebar"
      >
      <div className="p-4 relative">
        <Link
          to={productHref}
          className="block group rounded-xl border border-zinc-200 hover:border-violet-300 hover:shadow-sm transition-all overflow-hidden"
          data-testid={`featured-product-${current.slug}`}
        >
          {current.image_url ? (
            <div className="aspect-square bg-white">
              <img
                src={current.image_url}
                alt={current.name}
                className="w-full h-full object-contain p-3 group-hover:scale-[1.02] transition-transform"
                loading="lazy"
              />
            </div>
          ) : (
            <div className="aspect-square bg-zinc-50" />
          )}
          <div className="px-3 pb-3 pt-1">
            <h4 className="font-semibold text-zinc-900 text-base leading-snug tracking-tight line-clamp-2">
              {current.name}
            </h4>
            <p className="text-xs text-zinc-600 mt-2 leading-relaxed line-clamp-4">
              {current.excerpt}
            </p>
          </div>
        </Link>

        {items.length > 1 && (
          <>
            <button
              type="button"
              onClick={() => setIdx(safeIdx - 1)}
              className="absolute top-[40%] -left-1 -translate-y-1/2 w-9 h-9 rounded-full bg-white border border-zinc-200 shadow-md flex items-center justify-center text-zinc-600 hover:text-violet-700 hover:border-violet-300"
              aria-label="Previous featured product"
              data-testid="featured-sidebar-prev"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              type="button"
              onClick={() => setIdx(safeIdx + 1)}
              className="absolute top-[40%] -right-1 -translate-y-1/2 w-9 h-9 rounded-full bg-white border border-zinc-200 shadow-md flex items-center justify-center text-zinc-600 hover:text-violet-700 hover:border-violet-300"
              aria-label="Next featured product"
              data-testid="featured-sidebar-next"
            >
              <ChevronRight size={18} />
            </button>
          </>
        )}

        {/* Dot indicators */}
        {items.length > 1 && (
          <div className="flex items-center justify-center gap-1.5 mt-3">
            {items.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setIdx(i)}
                aria-label={`Show featured ${i + 1}`}
                data-testid={`featured-sidebar-dot-${i}`}
                className={`h-2 rounded-full transition-all ${
                  i === safeIdx
                    ? "bg-emerald-600 w-4"
                    : "bg-zinc-300 w-2 hover:bg-zinc-400"
                }`}
              />
            ))}
          </div>
        )}

        <Link
          to={shopHref}
          className="block text-center text-sm font-semibold text-emerald-700 hover:text-emerald-800 mt-3"
          data-testid="featured-sidebar-see-all"
        >
          See all products →
        </Link>
      </div>
      </div>
    </div>
  );
}
